import React, { createContext, useContext, useState, useEffect } from 'react';
import { FLAGS } from '../config/flags';
import { api } from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
      
      if (storedToken) {
        setToken(storedToken);
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }

        // Fetch fresh user data if NOT in mock mode
        if (!FLAGS.USE_MOCK_API) {
          try {
            // Use /deriv/my-accounts to get both profile and linking status
            const response = await api.get('/deriv/my-accounts');
            const { user: apiUser, has_deriv_account, deriv_account } = response.data?.data || response.data;
            
            // Fix: Flatten role if it's an object
            let roleValue = apiUser.role;
            if (typeof roleValue === 'object' && roleValue !== null) {
              roleValue = roleValue.name || roleValue.slug || roleValue.title || 'Unknown';
            }

            // Failsafe: If has_deriv_account is false/missing but deriv_account object exists, assume linked
            const isDerivLinked = !!has_deriv_account || (deriv_account && Object.keys(deriv_account).length > 0);

            const userData = {
              ...apiUser,
              role: roleValue,
              roles: apiUser.roles, // Preserve full roles array for isAdmin checks
              deriv_linked: isDerivLinked
            };

            setUser(userData);
            localStorage.setItem('user_data', JSON.stringify(userData));
          } catch (err) {
            console.error('Failed to fetch user profile:', err);
            // If token is invalid (401), logout
            if (err.status === 401) {
              logout();
            }
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (userData, accessToken, rememberMe = false) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('access_token', accessToken);
    storage.setItem('user_data', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
  };

  const logout = async () => {
    if (!FLAGS.USE_MOCK_API && token) {
      try {
        await api.post('/logout');
      } catch (err) {
        console.error('Server logout failed:', err);
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user_data');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
